// «История» of a record (R-U22, um/2026-10-31/01 п.9): one feed of what happened to it — the journal's changes, letters and the form
// answer — with the saved revisions (R-C17) on the lines of the saves that made them and the published versions (platform П5) by time.
// A revision and a version are compared with the record now and brought back from their line; publishing sits on top when the section
// publishes its content.

import { useEffect, useState } from 'react';
import { formatPlain, type FieldDef, type Id, type MetaIndex, type RecordObj } from '@confdeck/core-shared';
import { ago } from '../fields/format.ts';
import { useSetting } from '../settings/use-setting.ts';
import { useMeta, useRecord, useSync } from '../sync/react.tsx';
import { Icon } from '../ui/icons.tsx';
import { Button, Input, useToast } from '../ui/primitives.tsx';
import { mergeFeed } from './history-feed.ts';
import { HistoryEvent, type HistoryEntry } from './HistoryEvent.tsx';
import r from './record.module.css';
import s from './history.module.css';

interface Version { id: Id; number: number; snapshot: Record<string, unknown>; note: string | null; author: string | null; published: boolean; createdAt: string }
interface Save { number: number; snapshot: Record<string, unknown>; author: string | null; command: string | null; reason: string | null; createdAt: string }

const text = (v: unknown) => (v == null ? '' : typeof v === 'string' ? v : JSON.stringify(v));
/** Fields people edit: what a revision brings back (managed fields belong to actions, relations are links). */
const editable = (meta: MetaIndex, typeId: Id) => meta.fields(typeId).filter((f) => !f.options.managed && f.fieldType !== 'relation');

export function History({ recordId }: { recordId: Id }) {
  const client = useSync();
  const meta = useMeta();
  const toast = useToast();
  const rec = useRecord(recordId);
  const publishSetting = useSetting('revisions.publish', rec?.typeId ?? null);
  const [events, setEvents] = useState<HistoryEntry[] | null>(null);
  const [saved, setSaved] = useState<{ items: Version[]; revisions: Save[] }>({ items: [], revisions: [] });
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const load = async () => {
    const [h, v] = await Promise.all([fetch(`/api/records/${recordId}/history`), fetch(`/api/records/${recordId}/revisions`)]);
    if (!h.ok) throw new Error('Не удалось загрузить историю');
    setEvents(((await h.json()) as { items: HistoryEntry[] }).items);
    setSaved(v.ok ? ((await v.json()) as { items: Version[]; revisions: Save[] }) : { items: [], revisions: [] });
  };
  // A save of the record adds an event and a revision: the feed follows the record's own changes.
  useEffect(() => {
    let alive = true;
    load().catch((e: Error) => alive && setError(e.message));
    return () => { alive = false; };
  }, [recordId, rec?.rev]);

  if (error) return <div className={r.empty}>{error}</div>;
  if (!rec || !events) return <div className={r.empty}>&nbsp;</div>;
  const declared = meta.type(rec.typeId)?.revisions ?? [];
  const versionFields: FieldDef[] = declared.length ? declared.map((k) => meta.field(rec.typeId, k)).filter((f): f is FieldDef => !!f) : publishSetting === true ? editable(meta, rec.typeId) : [];
  const published = saved.items.find((v) => v.published);
  const changed = !published || versionFields.some((f) => text(rec.data[f.key]) !== text(published.snapshot[f.key]));
  const latest = saved.revisions[0]?.number;
  const run = async (command: string, input: unknown, ok: string) => {
    const res = await client.run(command, input);
    toast(res.ok ? { kind: 'info', text: ok } : { kind: 'error', text: res.error.message });
    if (res.ok) { setNote(''); await load().catch(() => undefined); }
  };
  const feed = mergeFeed(events, saved.revisions, saved.items);

  const saveTools = (sv: Save) => sv.number === latest ? null : (
    <Tools id={`r${sv.number}`} open={open} onOpen={setOpen} rec={rec} snapshot={sv.snapshot} fields={editable(meta, rec.typeId)}
      same="Поля совпадают с текущими; отличаются связи или поля действий — их ревизия не возвращает"
      restore={{ label: 'Вернуть', run: () => void run('revision.restore', { id: recordId, number: sv.number }, `Поля вернулись к ревизии ${sv.number}`) }} />
  );

  return (
    <div className={s.box}>
      {versionFields.length > 0 && (
        <div className={s.publishBox}>
          <div className={s.state}>
            {published ? <>Опубликована версия {published.number}{changed ? ' · сохранены изменения, ещё не опубликованы' : ' · опубликовано всё сохранённое'}</> : 'Сохранено, ещё не опубликовано'}
          </div>
          {changed && (
            <div className={s.publish}>
              <Input value={note} placeholder="Что изменилось (необязательно)" aria-label="Заметка к версии" onChange={(e) => setNote(e.target.value)} />
              <Button size="sm" variant="primary" onClick={() => void run('content.publish', { id: recordId, note: note.trim() || undefined }, 'Опубликовано')}>Опубликовать</Button>
            </div>
          )}
        </div>
      )}
      {!feed.length ? <div className={r.empty}>Изменений пока нет</div> : (
        <ol className={r.history}>
          {feed.map((it) => {
            if (it.event) return <HistoryEvent key={`e${it.event.syncId}-${it.event.kind}-${it.at}`} e={it.event} rec={rec}>{it.save && saveTools(it.save)}</HistoryEvent>;
            if (it.version) {
              const v = it.version;
              return (
                <li key={`v${v.number}`} className={r.hItem}>
                  <Icon name="tag" size={16} />
                  <div className={r.hBody}>
                    <div><b>Версия {v.number}</b>{v.published && <span className={s.badge}>опубликована</span>}{v.author ? ` · ${v.author}` : ''} <span className={r.hTime}>{ago(v.createdAt)}</span></div>
                    {v.note && <div className={r.hLine}>{v.note}</div>}
                    <Tools id={`v${v.number}`} open={open} onOpen={setOpen} rec={rec} snapshot={v.snapshot} fields={versionFields} same="Совпадает с текущей записью"
                      restore={{ label: 'Вернуть в черновик', run: () => void run('content.restore', { id: recordId, number: v.number }, `Версия ${v.number} в черновике — опубликуйте, когда будет готово`) }} />
                  </div>
                </li>
              );
            }
            const sv = it.save!;
            return (
              <li key={`s${sv.number}`} className={r.hItem}>
                <span className={r.hDot} />
                <div className={r.hBody}>
                  <div><b>{sv.command === null ? 'Как было до первой правки' : `Сохранение${sv.author ? ` · ${sv.author}` : ''}`}</b> <span className={r.hTime}>{ago(sv.createdAt)}</span></div>
                  {sv.reason && <div className={r.hLine}>Причина: {sv.reason}</div>}
                  {saveTools(sv)}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

/** A revision's or a version's line tools: compare with the record now, side by side, and bring it back. */
function Tools({ id, open, onOpen, rec, snapshot, fields, same, restore }: {
  id: string; open: string | null; onOpen: (id: string | null) => void; rec: RecordObj; snapshot: Record<string, unknown>; fields: FieldDef[]; same: string;
  restore: { label: string; run: () => void };
}) {
  const meta = useMeta();
  const shown = open === id;
  const diff = shown ? fields.filter((f) => text(snapshot[f.key]) !== text(rec.data[f.key])) : [];
  return (
    <>
      <div className={s.tools}>
        <Button size="sm" variant="ghost" onClick={() => onOpen(shown ? null : id)}>{shown ? 'Скрыть' : 'Сравнить с текущей'}</Button>
        <Button size="sm" variant="ghost" onClick={restore.run}>{restore.label}</Button>
      </div>
      {shown && (diff.length ? diff.map((f) => (
        <div key={f.key} className={s.diff}>
          <div className={s.field}>{f.name}</div>
          <div className={s.cols}>
            <pre className={s.old}>{formatPlain(f, snapshot[f.key], meta) || text(snapshot[f.key]) || 'пусто'}</pre>
            <pre className={s.new}>{formatPlain(f, rec.data[f.key], meta) || text(rec.data[f.key]) || 'пусто'}</pre>
          </div>
        </div>
      )) : <div className={s.hint}>{same}</div>)}
    </>
  );
}
